import { ArrowRightIcon, CalendarIcon } from '@heroicons/react/20/solid'
import { localDateDaysAgo, toLocalISODate, type DateRange } from '@/lib/submissionFilter'

const MS_DAY = 86_400_000

interface RangePreset {
  label: string
  days: number
}

const PRESETS: readonly RangePreset[] = [
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
]

function formatRangeDate(iso: string, withYear: boolean): string {
  if (!iso) {
    return '—'
  }
  const date = new Date(`${iso}T00:00:00`)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  })
}

interface DateRangePickerProps {
  range: DateRange
  onChange: (range: DateRange) => void
}

/**
 * From/To day pickers with quick presets and a live read-out, shown when the
 * time period is "Custom range". Bounds are local `YYYY-MM-DD` days so they line
 * up with `filterSubmissions`'s day comparison.
 */
export function DateRangePicker({ range, onChange }: DateRangePickerProps): JSX.Element {
  const now = Date.now()
  const today = toLocalISODate(now)

  const spanDays =
    range.from && range.to
      ? Math.max(
          1,
          Math.round((new Date(range.to).getTime() - new Date(range.from).getTime()) / MS_DAY) + 1,
        )
      : null
  const crossYear = Boolean(range.from && range.to && range.from.slice(0, 4) !== range.to.slice(0, 4))

  const fieldClass =
    'peer w-full rounded-lg bg-navy-dark py-2.5 pl-9 pr-2 text-sm font-medium text-white outline-none ring-1 ring-white/10 transition focus:ring-2 focus:ring-neon-cyan [color-scheme:dark]'

  return (
    <div className="mt-2.5 rounded-xl bg-navy-light/50 p-3 ring-1 ring-white/10">
      <div className="mb-3 flex gap-2">
        {PRESETS.map((preset) => {
          const from = localDateDaysAgo(preset.days, now)
          const selected = range.from === from && range.to === today
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => onChange({ from, to: today })}
              aria-pressed={selected}
              className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-neon-cyan/60 ${
                selected
                  ? 'bg-neon-cyan/20 text-neon-cyan ring-1 ring-neon-cyan/50'
                  : 'bg-navy-dark text-gray-400 hover:text-white'
              }`}
            >
              {preset.label}
            </button>
          )
        })}
      </div>

      <div className="flex items-end gap-2">
        <label className="block min-w-0 flex-1">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-400">
            From
          </span>
          <div className="relative">
            <CalendarIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 peer-focus:text-neon-cyan" />
            <input
              type="date"
              aria-label="From date"
              value={range.from}
              max={range.to || today}
              onChange={(event) => onChange({ ...range, from: event.target.value })}
              className={fieldClass}
            />
          </div>
        </label>

        <ArrowRightIcon className="mb-2.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />

        <label className="block min-w-0 flex-1">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-400">
            To
          </span>
          <div className="relative">
            <CalendarIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 peer-focus:text-neon-cyan" />
            <input
              type="date"
              aria-label="To date"
              value={range.to}
              min={range.from || undefined}
              max={today}
              onChange={(event) => onChange({ ...range, to: event.target.value })}
              className={fieldClass}
            />
          </div>
        </label>
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-2.5">
        <p className="flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-300">
          <span className="font-semibold text-white">{formatRangeDate(range.from, crossYear)}</span>
          <ArrowRightIcon className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
          <span className="font-semibold text-white">{formatRangeDate(range.to, crossYear)}</span>
        </p>
        {spanDays !== null && (
          <span className="rounded-full bg-navy-dark px-2 py-0.5 text-[11px] font-medium text-neon-cyan">
            {spanDays} {spanDays === 1 ? 'day' : 'days'}
          </span>
        )}
      </div>
    </div>
  )
}
